import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import {
  ChecklistNavFooter,
  ComplianceReviewBreadcrumb,
  DocumentIssuesPanel,
  DocumentTabs,
  DocumentTitleBar,
  DocumentViewer,
  FolderSuggestionsCard,
  NeedsReviewChecklist,
  OtherDocumentsList,
  PropertySummaryCard,
} from './components';
import { useAgentComplianceReview } from './hooks/useAgentComplianceReview';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewMobileProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document tab (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * Mobile single-pane layout for the agent compliance review page.
 *
 * - **Overview** (no `?documentId=`): the property summary, folder suggestions,
 *   the "Needs review" checklist, and the "Other documents" list, full width.
 * - **Detail** (a checklist item open): a {@link ComplianceReviewBreadcrumb}
 *   back to the overview, the item title + actions, a document tab strip, the
 *   {@link DocumentViewer}, the {@link DocumentIssuesPanel} stacked below, and a
 *   pinned footer to cycle Needs-Review items.
 *
 * Behavior comes from {@link useAgentComplianceReview}, shared with desktop. The
 * mobile flow keeps the overview list as the landing (no auto-select).
 */
export const AgentComplianceReviewMobile: FC<
  AgentComplianceReviewMobileProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const {
    document,
    loading,
    error,
    onPdfLoadError,
    activeFieldId,
    validation,
    hasSelection,
    needsReviewItems,
    otherItems,
    activeItem,
    activeItemDocs,
    tabSuggestionCounts,
    itemNeedsReview,
    itemResubmitted,
    isResubmitReady,
    platformRequirementIssues,
    platformUrl,
    onOpenPlatform,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    showChecklistNav,
    hasPrevChecklistItem,
    hasNextChecklistItem,
    onSelectNextChecklistItem,
    onSelectPrevChecklistItem,
    onSelectFields,
    onSelectDocument,
    onClearSelection,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  } = useAgentComplianceReview({
    overview,
    selectedDocumentId,
    token,
    ingestionFolderUuid,
  });

  // Overview: full-width single column.
  if (!hasSelection) {
    return (
      <Box
        bg="compliance.sidebar.bg"
        h="100dvh"
        maxH="100dvh"
        overflowY="auto"
        w="full"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <FolderSuggestionsCard
            isIgnoring={isIgnoring}
            suggestions={overview.folderSuggestions}
            onIgnore={onIgnoreFolderSuggestion}
            onUndoIgnore={onUndoIgnoreFolderSuggestion}
          />

          <NeedsReviewChecklist
            activeItemId={activeItem?.id}
            items={needsReviewItems}
            onSelectDocument={onSelectDocument}
          />

          <OtherDocumentsList
            activeItemId={activeItem?.id}
            items={otherItems}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </Box>
    );
  }

  const propertyLabel =
    [
      overview.property?.streetAddress?.trim(),
      overview.property?.cityState?.trim(),
    ]
      .filter(Boolean)
      .join(' ') || overview.folderName;

  // Detail: breadcrumb + item title/actions + tabs + PDF + issues, with a pinned
  // Needs-Review nav footer.
  return (
    <Flex
      bg="compliance.background.primary"
      direction="column"
      h="100dvh"
      maxH="100dvh"
      overflow="hidden"
      w="full"
    >
      <Box
        borderBottomColor="compliance.border.primary"
        borderBottomWidth={1}
        flexShrink={0}
        px={4}
        py={3}
      >
        <ComplianceReviewBreadcrumb
          documentName={activeItem?.title}
          propertyLabel={propertyLabel}
          onBack={onClearSelection}
        />
      </Box>

      {error ? (
        <Box p={4}>
          <Text color="compliance.notification.error.text" fontSize="sm">
            We couldn&apos;t load this document. Please try again.
          </Text>
        </Box>
      ) : (
        <>
          <Box flex={1} minH={0} overflowY="auto">
            {activeItem != null && (
              <DocumentTitleBar
                defaultResubmitMessage={defaultResubmitMessage}
                isResubmitReady={isResubmitReady}
                isResubmitted={itemResubmitted}
                isSubmitting={isSubmitting}
                needsReview={itemNeedsReview}
                platformUrl={platformUrl}
                title={activeItem.title}
                onOpenPlatform={onOpenPlatform}
                onResubmit={onResubmit}
              />
            )}

            <DocumentTabs
              docs={activeItemDocs}
              selectedDocumentId={selectedDocumentId}
              suggestionCounts={tabSuggestionCounts}
              onSelectDocument={onSelectDocument}
            />

            <Box h="60dvh" minH="60dvh">
              <DocumentViewer
                activeFieldId={activeFieldId}
                documentName={document?.documentName}
                loading={loading}
                pdfUrl={document?.pdfUrl}
                submittedAt={
                  document?.submittedAt != null
                    ? String(document.submittedAt)
                    : undefined
                }
                successFieldIds={validation.successFieldIds}
                validationFieldsMap={validation.validationFieldsMap}
                warningFieldIds={validation.errorFieldIds}
                warningFieldMessages={validation.fieldMessages}
                onPdfLoadError={onPdfLoadError}
              />
            </Box>

            <Box
              bg="compliance.sidebar.bg"
              borderTopColor="compliance.sidebar.border"
              borderTopWidth={1}
              p={4}
            >
              <DocumentIssuesPanel
                contractDocumentCount={validation.contractDocumentCount}
                hasDetail={document != null}
                isIgnoring={isIgnoring}
                isIgnoringPlatformRequirementIssue={isIgnoringPlatformIssue}
                loading={loading}
                platformRequirementIssues={platformRequirementIssues}
                successfulChecks={validation.successfulChecks}
                suggestions={validation.suggestions}
                onIgnorePlatformRequirementIssue={
                  onIgnorePlatformRequirementIssue
                }
                onIgnoreSuggestion={onIgnoreDocumentSuggestion}
                onSelectFields={onSelectFields}
                onUndoIgnorePlatformRequirementIssue={
                  onUndoIgnorePlatformRequirementIssue
                }
                onUndoIgnoreSuggestion={onUndoIgnoreDocumentSuggestion}
              />
            </Box>
          </Box>

          {showChecklistNav && (
            <ChecklistNavFooter
              hasNext={hasNextChecklistItem}
              hasPrev={hasPrevChecklistItem}
              onNext={onSelectNextChecklistItem}
              onPrev={onSelectPrevChecklistItem}
            />
          )}
        </>
      )}
    </Flex>
  );
};
